import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Box,
  Container,
  Paper,
  Fade,
  Grow,
  Card,
  CardContent,
  Grid,
  Button,
  Popover,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Menu,
  MenuItem,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { 
  Security as SecurityIcon,
  People as PeopleIcon,
  Videocam as VideocamIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { format, addDays, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Bar,
  Cell,
  ReferenceLine,
  Legend,
} from 'recharts';
import { getStoredToken, authenticatedFetch, getROICameras, getCameraStreams } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import IntrusionDailyStatsCard from './IntrusionDailyStatsCard';

const GREEN_COLOR = '#10b981'; // Positive values (enrollments)
const RED_COLOR = '#ef4444';   // Negative values (deletions)

const WEBVIEW_BTN_SX = {
  background: '#ffffff',
  color: '#1f2937',
  border: '1px solid rgba(64, 81, 138, 0.12)',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: { xs: 200, sm: 230 },
  minHeight: { xs: 42, sm: 48 },
  px: { xs: 2, sm: 3 },
  py: { xs: 1, sm: 1.5 },
  boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
  '&:hover': {
    background: '#ffffff',
    borderColor: 'rgba(64, 81, 138, 0.22)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
    color: '#111827',
  },
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    color: '#40518A',
  },
  transition: 'all 0.2s ease',
};

const emptyCustomForm = () => ({
  name: '',
  webview_type: 'intrusion',
  inCameras: [],
  outCameras: [],
});

const cameraOptionLabel = (camera) =>
  camera?.label || camera?.camera_label || camera?.id || 'Camera';

const IntrusionDashboard = () => {
  const navigate = useNavigate();
  const { userType, permissions } = useAuth();
  const [intrusionData, setIntrusionData] = useState(null);
  const [accountStats, setAccountStats] = useState([]);
  const [loading, setLoading] = useState();
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  
  // Enrollment analytics state
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState(null);
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -6),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const datePickerOpen = Boolean(datePickerAnchor);
  const [customWebviews, setCustomWebviews] = useState([]);
  const [inCameraOptions, setInCameraOptions] = useState([]);
  const [outCameraOptions, setOutCameraOptions] = useState([]);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customForm, setCustomForm] = useState(emptyCustomForm);
  const [editingWebview, setEditingWebview] = useState(null);
  const [customSaving, setCustomSaving] = useState(false);
  const [customError, setCustomError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuWebview, setMenuWebview] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const visibleCustomWebviews = useMemo(
    () => customWebviews.filter((item) => (
      item.webview_type === 'intrusion' || item.webview_type === 'combined'
    )),
    [customWebviews]
  );

  const loadCustomWebviews = async () => {
    try {
      const response = await authenticatedFetch('/camera-streams/custom-webviews');
      const data = await response.json();
      setCustomWebviews(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      console.error('Failed to load custom webviews:', err);
    }
  };

  const loadAssignedCameras = async () => {
    const toOptions = (cameras, type) => (
      (Array.isArray(cameras) ? cameras : [])
        .filter((cam) => String(cam.camera_type || '').toUpperCase() === type)
        .map((cam) => ({
          id: cam.camera_id || cam.id,
          label: cam.label || cam.camera_label || cam.camera_id || cam.id,
          camera_type: type,
        }))
        .filter((cam) => cam.id)
    );
    let inOpts = [];
    let outOpts = [];
    try {
      const roi = await getROICameras();
      inOpts = toOptions(roi?.cameras, 'IN');
      outOpts = toOptions(roi?.cameras, 'OUT');
      if (!inOpts.length && !outOpts.length) {
        const streams = await getCameraStreams();
        const items = Array.isArray(streams?.items) ? streams.items : [];
        const active = items.filter((cam) => cam.is_active && (cam.camera_type === 'IN' || cam.camera_type === 'OUT'));
        inOpts = toOptions(active, 'IN');
        outOpts = toOptions(active, 'OUT');
      }
      setInCameraOptions(inOpts);
      setOutCameraOptions(outOpts);
    } catch (err) {
      console.error('Failed to load assigned cameras:', err);
    }
    return { inOpts, outOpts };
  };


  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }
    
    // Fetch company name from banner settings (optional, don't fail if it errors)
    const fetchCompanyName = async () => {
      try {
        const response = await authenticatedFetch(`/settings/company`);
        const data = await response.json();
        if (data.company_name) {
          setCompanyName(data.company_name);
        }
      } catch (err) {
        // Silently fail - company name is optional
        console.log('Company name not available, using default');
        setCompanyName('Intrusion Dashboard');
      }
    };

    // Fetch account stats
    const fetchAccountStats = async () => {
      try {
        const response = await authenticatedFetch('/dashboard/account-wise-profile-counts');
        const result = await response.json();
        if (result.status === 'success') {
          setAccountStats(result.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch account stats:', err);
      }
    };
    
    fetchCompanyName();
    fetchAccountStats();
    fetchEnrollmentAnalytics();
    loadCustomWebviews();
    loadAssignedCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fetch enrollment analytics
  const fetchEnrollmentAnalytics = async () => {
    try {
      setEnrollmentLoading(true);
      setEnrollmentError(null);
      
      const params = new URLSearchParams();
      const startDate = format(dateRange[0].startDate, 'yyyy-MM-dd');
      const endDate = format(dateRange[0].endDate, 'yyyy-MM-dd');
      
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      params.append('is_intrusion', 'true'); // Filter for intrusion profiles only
      
      const response = await authenticatedFetch(`/dashboard/analytics?${params}`);
      const data = await response.json();
      setEnrollmentData(data);
    } catch (err) {
      console.error('Enrollment analytics fetch error:', err);
      setEnrollmentError(err.message || 'Unable to load enrollment data');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  // Prepare chart data for enrollment
  const prepareEnrollmentChartData = () => {
    if (!enrollmentData) {
      return [];
    }

    const dailyCounts = enrollmentData.daily_counts || [];
    
    // Fill in missing dates in the range
    const startDate = new Date(dateRange[0].startDate);
    const endDate = new Date(dateRange[0].endDate);
    const allDates = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const existingData = dailyCounts.find(d => d.date === dateStr);
      allDates.push({
        date: dateStr,
        displayDate: format(currentDate, 'MMM dd'),
        count: existingData ? existingData.count : 0,
        enrollments: existingData ? existingData.enrollments : 0,
        deletions: existingData ? existingData.deletions : 0,
        index: allDates.length,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  };

  const summary = intrusionData?.summary || {};
  const {
    total_in = 0,
    total_out = 0,
    in_premises = 0,
    out_premises = 0,
    late_entries = 0,
    total_unique_profiles = 0,
  } = summary;

  // Calculate total present and total from accountStats
  const totalPresent = accountStats.reduce((sum, account) => sum + (account.present_count || 0), 0);
  const totalCount = accountStats.reduce((sum, account) => sum + (account.total_count || 0), 0);
  const intrusionLogsPath =
    userType === 'intrusion' || permissions?.dashboard_path === '/intrusion-dashboard'
      ? '/intrusion-logs'
      : '/intrusion';

  const camerasForIds = (options, ids) => {
    const wanted = new Set((ids || []).map((id) => String(id).toLowerCase()));
    return (options || []).filter((cam) => wanted.has(String(cam.id).toLowerCase()));
  };

  const openCreateModal = () => {
    setEditingWebview(null);
    setCustomForm(emptyCustomForm());
    setCustomError('');
    loadAssignedCameras();
    setCustomModalOpen(true);
  };

  const openEditModal = async (item) => {
    setEditingWebview(item);
    setCustomForm({
      name: item.name || '',
      webview_type: item.webview_type === 'combined' ? 'combined' : 'intrusion',
      inCameras: camerasForIds(inCameraOptions, item.in_camera_ids),
      outCameras: camerasForIds(outCameraOptions, item.out_camera_ids),
    });
    setCustomError('');
    setCustomModalOpen(true);
    const { inOpts, outOpts } = await loadAssignedCameras();
    setCustomForm((prev) => ({
      ...prev,
      inCameras: camerasForIds(inOpts, item.in_camera_ids),
      outCameras: camerasForIds(outOpts, item.out_camera_ids),
    }));
  };

  const closeCustomModal = () => {
    if (customSaving) return;
    setCustomModalOpen(false);
    setEditingWebview(null);
    setCustomError('');
  };

  const handleSaveCustomWebview = async () => {
    const name = String(customForm.name || '').trim();
    const inIds = customForm.inCameras.map((cam) => cam.id).filter(Boolean);
    const outIds = customForm.outCameras.map((cam) => cam.id).filter(Boolean);
    if (!name) {
      setCustomError('Enter a webview name.');
      return;
    }
    if (!inIds.length && !outIds.length) {
      setCustomError('Select at least one IN or OUT camera.');
      return;
    }
    setCustomSaving(true);
    setCustomError('');
    const payload = {
      name,
      webview_type: customForm.webview_type === 'combined' ? 'combined' : 'intrusion',
      in_camera_ids: inIds,
      out_camera_ids: outIds,
    };
    try {
      if (editingWebview?.id) {
        await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(editingWebview.id)}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await authenticatedFetch('/camera-streams/custom-webviews', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      await loadCustomWebviews();
      setCustomModalOpen(false);
      setEditingWebview(null);
    } catch (err) {
      const detail = typeof err.detail === 'string' ? err.detail : err.message;
      setCustomError(detail || 'Failed to save custom webview.');
    } finally {
      setCustomSaving(false);
    }
  };

  const handleDeleteCustomWebview = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(deleteTarget.id)}`, {
        method: 'DELETE',
      });
      await loadCustomWebviews();
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete custom webview:', err);
    } finally {
      setDeleting(false);
    }
  };

  const openCustomWebview = async (item) => {
    let url = item?.webview_url;
    if (item?.id) {
      try {
        const latestRes = await authenticatedFetch(`/camera-streams/custom-webviews/${encodeURIComponent(item.id)}`);
        const latest = await latestRes.json();
        if (latest?.webview_url) {
          url = latest.webview_url;
          setCustomWebviews((prev) => prev.map((entry) => (
            entry.id === latest.id ? { ...entry, ...latest } : entry
          )));
        }
      } catch (err) {
        console.error('Failed to refresh custom webview URL:', err);
      }
    }
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderIntrusionDashboard = () => (
    <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          pt: 0,
          px: { xs: 1.5, sm: 2, md: 3, lg: 4 },
          width: '100%',
          maxWidth: '100%'
        }}
      >
        {/* Hero Header Section */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              background: '#40518A',
              borderRadius: { xs: 2, sm: 3, md: 4 },
              p: { xs: 1.5, sm: 2, md: 3 },
              mb: { xs: 2, sm: 2, md: 1 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
              width: '100%',
            }}
          >
            {/* Decorative Background Elements */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: -30, sm: -40, md: -50 },
                right: { xs: -30, sm: -40, md: -50 },
                width: { xs: 120, sm: 160, md: 200 },
                height: { xs: 120, sm: 160, md: 200 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                filter: 'blur(40px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: -20, sm: -25, md: -30 },
                left: { xs: -20, sm: -25, md: -30 },
                width: { xs: 100, sm: 120, md: 150 },
                height: { xs: 100, sm: 120, md: 150 },
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.08)',
                filter: 'blur(30px)',
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  mb: 0, 
                  flexWrap: 'wrap', 
                  gap: { xs: 1.5, sm: 2 },
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: { xs: 2, sm: 3 },
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    flexShrink: 0,
                  }}
                >
                  <SecurityIcon sx={{ color: 'white', fontSize: { xs: 32, sm: 36, md: 40 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                  <Typography 
                    variant="h3" 
                    component="h1"
                    sx={{ 
                      color: 'white',
                      fontWeight: 500,
                      mb: { xs: 0.5, sm: 1 },
                      fontSize: { xs: '1rem', sm: '1.35rem', md: '1.5rem', lg: '1.5rem' },
                      textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                      lineHeight: 1.2,
                    }}
                  >
                    {companyName || 'Intrusion Dashboard'}
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' },
                      fontWeight: 400,
                      mt: { xs: 0.5, sm: 0 },
                    }}
                  >
                    Comprehensive overview of your Intrusion monitoring system
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Fade>
        <Fade in timeout={900}>
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid rgba(64, 81, 138, 0.12)',
              borderRadius: { xs: 2, sm: 3 },
              p: { xs: 1.5, sm: 2, md: 2.5 },
              mb: { xs: 2, sm: 2, md: 1 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 28px rgba(31, 45, 84, 0.12)',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                startIcon={<VideocamIcon />}
                onClick={() => window.open('/webview-intrusion', '_blank', 'noopener,noreferrer')}
                sx={WEBVIEW_BTN_SX}
              >
                Intrusion Webview
              </Button>
              {visibleCustomWebviews.map((item) => (
                <Button
                  key={item.id}
                  variant="contained"
                  startIcon={<VideocamIcon />}
                  endIcon={
                    <MoreVertIcon
                      fontSize="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setMenuAnchor(event.currentTarget);
                        setMenuWebview(item);
                      }}
                    />
                  }
                  onClick={() => openCustomWebview(item)}
                  sx={{
                    ...WEBVIEW_BTN_SX,
                    '& .MuiButton-endIcon': { ml: 0.5 },
                  }}
                  aria-label={item.name}
                >
                  {item.name}
                </Button>
              ))}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateModal}
                sx={WEBVIEW_BTN_SX}
              >
                Add custom webview
              </Button>
            </Box>
          </Paper>
        </Fade>

        {/* Today's Intrusion Metrics */}
        <Grow in timeout={1000}>
          <Box
            sx={{
              width: '100%',
              mb: { xs: 2, sm: 2, md: 1 },
            }}
          >
            <Card sx={{ height: '100%' }}>
              <CardContent>
                {/* Present/Total Metric Card with Tooltip */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(1, 1fr)',
                      md: 'repeat(1, 1fr)'
                    },
                    gap: 2,
                  }}
                >
                  {accountStats.length > 0 ? (
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333', borderBottom: '1px solid #eee', pb: 1 }}>
                            Profiles by Account
                          </Typography>
                          {accountStats.map((account, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              sx={{ py: 0.5, color: '#333', fontWeight: 600 }}
                            >
                              {account.account_name || 'Unknown Account'} - {account.present_count || 0}/{account.total_count || 0}
                            </Typography>
                          ))}
                        </Box>
                      }
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'rgba(255, 255, 255, 0.98)',
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            '& .MuiTooltip-arrow': {
                              color: 'rgba(255, 255, 255, 0.98)',
                              '&::before': {
                                border: '1px solid #e0e0e0'
                              }
                            },
                            maxWidth: 320,
                            fontSize: '0.875rem',
                            borderRadius: '8px',
                            padding: '12px 16px',
                          }
                        }
                      }}
                    >
                      <Box
                        onClick={() => navigate(intrusionLogsPath)}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 2,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          backgroundColor: 'background.paper',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                          }
                        }}
                      >
                        {/* Icon + Label */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ color: GREEN_COLOR, fontSize: 24 }} />
                          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Recognized / Total Profiles
                          </Typography>
                        </Box>

                        {/* Value */}
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 600, color: GREEN_COLOR, mt: 1 }}
                        >
                          {totalPresent}/{totalCount}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Box
                      onClick={() => navigate(intrusionLogsPath)}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                        }
                      }}
                    >
                      {/* Icon + Label */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ color: GREEN_COLOR, fontSize: 24 }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Present/Total
                        </Typography>
                      </Box>

                      {/* Value */}
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, color: GREEN_COLOR, mt: 1 }}
                      >
                        {totalPresent}/{totalCount}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grow>

        {/* Intrusion Profile Enrollment and Daily Stats Row */}
        <Grow in timeout={1600}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              mb: 0,
            }}
          >
            {/* Intrusion Profile Enrollment Widget */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                flex: 1,
              }}
            >
            <Card sx={{ mb: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header and Date Range Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Intrusion Profile Enrollment</Typography>
                  </Box>

                  {/* Date Range Selector */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        padding: '8px 12px',
                        bgcolor: '#f9f9f9'
                      }}
                    >
                      <CalendarToday sx={{ color: '#666', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#333' }}>
                        {format(dateRange[0].startDate, 'MMM dd')} -{' '}
                        {format(dateRange[0].endDate, 'MMM dd')}
                      </Typography>
                      <IconButton size="small" onClick={(e) => setDatePickerAnchor(e.currentTarget)} sx={{ ml: 1 }}>
                        <CalendarToday fontSize="small" />
                      </IconButton>
                    </Box>
                    <Button variant="contained" onClick={fetchEnrollmentAnalytics} disabled={enrollmentLoading}>
                      Apply
                    </Button>
                  </Box>
                </Box>

                {/* Date Range Picker Popover */}
                <Popover
                  open={datePickerOpen}
                  anchorEl={datePickerAnchor}
                  onClose={() => setDatePickerAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                >
                  <Box sx={{ p: 0, width: 'fit-content' }}>
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      moveRangeOnFirstSelection={false}
                      months={1}
                      direction="horizontal"
                      rangeColors={['#40518A']}
                      staticRanges={[]}
                      inputRanges={[]}
                      maxDate={new Date()}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
                      <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => {
                          setDatePickerAnchor(null);
                          fetchEnrollmentAnalytics();
                        }} 
                        variant="contained" 
                        size="small"
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Popover>

                {/* Loading State */}
                {enrollmentLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Error State */}
                {enrollmentError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {enrollmentError}
                  </Alert>
                )}

                {/* Analytics Data */}
                {!enrollmentLoading && !enrollmentError && enrollmentData && (
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" color="text.secondary">
                        Net Registrations: <strong>{enrollmentData.total_registrations}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Date Range: {enrollmentData.date_range?.start_date} to{' '}
                        {enrollmentData.date_range?.end_date}
                      </Typography>
                    </Box>

                    {/* Bar Chart */}
                    {(() => {
                      const chartData = prepareEnrollmentChartData();
                      const minCount = chartData.length > 0 ? Math.min(...chartData.map(d => d.count)) : 0;
                      const maxCount = chartData.length > 0 ? Math.max(...chartData.map(d => d.count)) : 0;
                      const absMax = Math.max(Math.abs(minCount), Math.abs(maxCount));
                      const paddedMax = Math.ceil(absMax + absMax * 0.1) + 1;
                      const yAxisDomain = [-paddedMax, paddedMax];

                      return chartData.length > 0 ? (
                        <Box sx={{ flexGrow: 1, minHeight: 400 }}>
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                              data={chartData.map((item, index) => ({
                                ...item,
                                index: index + 1,
                              }))}
                              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
                              <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
                              <XAxis
                                dataKey="index"
                                tick={false}
                                axisLine={false}
                                height={10}
                              />
                              <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: '#666' }}
                                axisLine={{ stroke: '#ccc' }}
                                tickCount={9}
                                domain={yAxisDomain}
                              />
                              <RechartsTooltip
                                cursor={{ fill: 'rgba(124, 26, 255, 0.1)' }}
                                content={({ active, payload }) => {
                                  if (!active || !payload || !payload.length) return null;
                                  const item = payload[0].payload;
                                  return (
                                    <div style={{
                                      backgroundColor: 'white',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      padding: '8px',
                                      fontSize: '13px'
                                    }}>
                                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Date: {item.displayDate}</div>
                                      <div>Net Change: {item.count}</div>
                                      <div style={{ fontSize: '0.85em', color: '#666' }}>Enrollments: {item.enrollments || 0}</div>
                                      <div style={{ fontSize: '0.85em', color: '#666' }}>Deletions: {item.deletions || 0}</div>
                                    </div>
                                  );
                                }}
                              />
                              <Bar
                                dataKey="count"
                                name="Net Change"
                                radius={[4, 4, 0, 0]}
                              >
                                {chartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.count >= 0 ? GREEN_COLOR : RED_COLOR}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 400,
                            border: '1px dashed #ccc',
                            borderRadius: 1,
                            bgcolor: '#f5f5f5'
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            No registration data available for the selected date range
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
              </CardContent>
            </Card>
            </Box>

            {/* Intrusion Daily Stats Bar Chart */}
            <Box
              sx={{
                width: { xs: '100%', md: '50%' },
                flex: 1,
              }}
            >
              <IntrusionDailyStatsCard />
            </Box>
          </Box>
        </Grow>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            </Paper>
          </Box>
        )}
      </Container>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuWebview(null);
        }}
      >
        <MenuItem
          onClick={() => {
            const item = menuWebview;
            setMenuAnchor(null);
            setMenuWebview(null);
            if (item) openEditModal(item);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            const item = menuWebview;
            setMenuAnchor(null);
            setMenuWebview(null);
            if (item) setDeleteTarget(item);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={customModalOpen} onClose={closeCustomModal} fullWidth maxWidth="sm">
        <DialogTitle>{editingWebview ? 'Edit custom webview' : 'Add custom webview'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Webview name"
            value={customForm.name}
            onChange={(event) => setCustomForm((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            margin="dense"
          />
          <RadioGroup
            row
            value={customForm.webview_type}
            onChange={(event) => setCustomForm((prev) => ({ ...prev, webview_type: event.target.value }))}
          >
            <FormControlLabel value="intrusion" control={<Radio />} label="Intrusion webview" />
            <FormControlLabel value="combined" control={<Radio />} label="Attendance + Intrusion" />
          </RadioGroup>
          <Autocomplete
            multiple
            options={inCameraOptions}
            value={customForm.inCameras}
            onChange={(_, value) => setCustomForm((prev) => ({ ...prev, inCameras: value }))}
            getOptionLabel={cameraOptionLabel}
            isOptionEqualToValue={(option, value) => String(option.id).toLowerCase() === String(value.id).toLowerCase()}
            renderInput={(params) => <TextField {...params} label="IN camera" placeholder="Select IN cameras" />}
          />
          <Autocomplete
            multiple
            options={outCameraOptions}
            value={customForm.outCameras}
            onChange={(_, value) => setCustomForm((prev) => ({ ...prev, outCameras: value }))}
            getOptionLabel={cameraOptionLabel}
            isOptionEqualToValue={(option, value) => String(option.id).toLowerCase() === String(value.id).toLowerCase()}
            renderInput={(params) => <TextField {...params} label="OUT camera" placeholder="Select OUT cameras" />}
          />
          {customError ? <FormHelperText error>{customError}</FormHelperText> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomModal} disabled={customSaving}>Cancel</Button>
          <Button onClick={handleSaveCustomWebview} variant="contained" disabled={customSaving}>
            {customSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete custom webview</DialogTitle>
        <DialogContent>
          <Typography>
            Delete “{deleteTarget?.name}”? This removes the dashboard button. Bookmarked camera URLs are unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCustomWebview} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        minHeight: '100vh',
      }}
    >
      {renderIntrusionDashboard()}
    </Box>
  );
};

export default IntrusionDashboard;
